import type { InfiniteData } from '@tanstack/react-query';

import type { PreferredTimezone } from 'data/generated/graphql';
import type { AllessehCollectionItem } from 'hooks/useAllessehCollectionQuery';
import type { AllessehContentItemAttributes, AllessehContentQueryResponse } from 'hooks/useAllessehContentQuery';
import { formatInTimeZone } from 'utils/dates';
import env from 'utils/env';

/**
 * Resolves input to an Allesseh ID.
 * If input is a slug (doesn't contain UCSP_ or UCSD_), looks it up in slug mappings.
 * If input is already an Allesseh ID, returns it as-is.
 */
export const resolveAllessehId = (input: string, slugMappings?: Record<string, unknown>): string => {
  if (!input.trim()) return '';

  // Check if it's an Allesseh ID (contains UCSP_ or UCSD_)
  const isAllessehId = /UCS[PD]_/.test(input);

  if (isAllessehId) {
    // It's already an Allesseh ID, use as-is
    return input;
  }

  // It's a slug, try to find the corresponding Allesseh ID
  if (slugMappings?.[input]) {
    return slugMappings[input] as string;
  }

  // Slug not found in mappings, return empty string to avoid API calls
  return '';
};

/**
 * Finds an embargo article by ID from the embargo articles data
 */
export const findEmbargoArticleById = (
  articleId: string,
  embargoArticlesData?: InfiniteData<AllessehContentQueryResponse>
): AllessehContentItemAttributes | null => {
  if (!embargoArticlesData) return null;

  const embargoItem = embargoArticlesData.pages
    .flatMap((page) => page.data?.attributes ?? [])
    .find((content) => content.data.id === articleId);

  return embargoItem?.data.attributes ?? null;
};

// Types for articles and resolvedArticles
export type Article = {
  type: string;
  id: string;
  base_doc_id?: string;
  collection?: Article[];
  headline?: string | null;
  title?: string | null;
  updated_datetime_utc?: string | null;
  content_status?: string | null;
  status?: string | null;
  product?: string | null;
  source_url?: string | null;
  parameters?: Array<{ name: string; value: unknown; type: string }>;
};

export type ResolvedArticle = {
  id: string;
  attributes?: {
    data?: {
      attributes?: AllessehContentItemAttributes;
    };
  };
};

export type EmbargoArticlesData =
  | {
      pages?: Array<{
        data?: {
          attributes?: Array<{
            data?: { id: string };
          }>;
        };
      }>;
    }
  | undefined;

// Type for collection items that can be nested
export type CollectionOrArticle = AllessehCollectionItem & {
  collection?: CollectionOrArticle[];
  headline?: string;
  updated_datetime_utc?: string;
  base_doc_id?: string; // Some articles have base_doc_id instead of id
  status?: string; // Article status (e.g., 'embargo', 'live')
};

const extractArticlesFromRawData = (items: CollectionOrArticle[]): Set<string> => {
  const articleIds = new Set<string>();

  const extractFromItems = (collection: CollectionOrArticle[]) => {
    collection.forEach((item) => {
      if (item.type !== 'collection' && item.type !== 'link') {
        const { id, base_doc_id: baseDocId } = item;
        if (typeof id === 'string') {
          articleIds.add(id);
        } else if (typeof baseDocId === 'string') {
          articleIds.add(baseDocId);
        }
      }
      if (item.collection && Array.isArray(item.collection)) {
        extractFromItems(item.collection);
      }
    });
  };

  extractFromItems(items);
  return articleIds;
};

// Function to extract all articles from resolved data (included array)
const extractArticlesFromResolvedData = (collectionSummaryData: any): Set<string> => {
  const articleIds = new Set<string>();

  if (
    collectionSummaryData &&
    typeof collectionSummaryData === 'object' &&
    Array.isArray((collectionSummaryData as { included?: unknown }).included)
  ) {
    (collectionSummaryData as { included: Array<{ id: string }> }).included.forEach((item) => {
      if (item.id) {
        articleIds.add(item.id);
      }
    });
  }

  return articleIds;
};
const parseUrlForAllessehId = (url: string): string => {
  if (!url) return '';
  try {
    // Handle full URLs
    if (url.startsWith('http')) {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/').filter(Boolean);

      if (pathParts.length >= 3) {
        // Format: /publication/pages/slug or /publication/pages/ALLESSEH_ID
        return pathParts[2];
      }
    } else if (url.includes('/')) {
      // Handle relative paths like "wsj/pages/business"
      const pathParts = url.split('/').filter(Boolean);
      if (pathParts.length >= 3) {
        return pathParts[2];
      }
    } else {
      // Assume it's already an Allesseh ID or slug
      return url;
    }
  } catch (error) {
    console.error('Error parsing URL:', error);
  }

  return url;
};

const formatDate = (timestamp: number | string, preferredTimezone: PreferredTimezone | null): string => {
  if (!preferredTimezone) {
    return new Date(timestamp).toLocaleString();
  }

  try {
    return formatInTimeZone(new Date(timestamp), preferredTimezone, 'MMM dd, yyyy HH:mm:ss zzz');
  } catch {
    return 'Invalid Date';
  }
};

// Reuse Allesseh admin URL logic from ExternalLink (Storycards/Edit page)
const getAllessehAdminUrl = (contentId: string, type: 'article' | 'collection' = 'article', embargo = false) => {
  if (!env.ALLESSEH_ADMIN_TOOL) return null;
  const EMBARGO_PATH = 'preview/content';
  const LIVE_PATH = `${type === 'article' ? 'history' : 'collections'}/byoriginid`;
  const PATH = embargo ? EMBARGO_PATH : LIVE_PATH;
  return `${env.ALLESSEH_ADMIN_TOOL}${PATH}/${contentId}`;
};

// Get Allesseh Collection Summaries API URL
const getAllessehSummariesUrl = (contentId: string) => {
  if (!env.ALLESSEH_API_COLLECTION_SUMMARIES_URL) return null;
  return `${env.ALLESSEH_API_COLLECTION_SUMMARIES_URL}/${contentId}`;
};

const extractEmbargoArticlesFromRawData = (rawData: CollectionOrArticle[]): Set<string> => {
  const embargoArticleIds = new Set<string>();

  const extractEmbargoFromItems = (collection: CollectionOrArticle[]) => {
    collection.forEach((item) => {
      if (item.type !== 'collection' && item.type !== 'link') {
        if (item.status === 'embargo') {
          const id = item.base_doc_id;
          if (id) {
            embargoArticleIds.add(id);
          }
        }
      }
      if (item.collection && Array.isArray(item.collection)) {
        extractEmbargoFromItems(item.collection);
      }
    });
  };

  extractEmbargoFromItems(rawData);
  return embargoArticleIds;
};

// Function to find article details from raw data
const findArticleInRawData = (targetId: string, items: unknown[]): unknown => {
  if (!Array.isArray(items)) return null;

  // Helper type guard for item object
  const isCollectionItem = (
    item: unknown
  ): item is { type?: string; id?: unknown; base_doc_id?: unknown; collection?: unknown } =>
    typeof item === 'object' && item !== null;

  // Search for the article in the current array
  const foundItem = items.find((item) => {
    if (!isCollectionItem(item)) return false;
    if (item.type !== 'collection') {
      const id =
        typeof item.id === 'string' ? item.id : typeof item.base_doc_id === 'string' ? item.base_doc_id : undefined;
      return id === targetId;
    }
    return false;
  });

  if (foundItem) {
    return foundItem;
  }

  // Recursively search in nested collections
  let foundArticle: unknown = null;
  items.forEach((item) => {
    if (foundArticle) return;
    if (isCollectionItem(item) && Array.isArray(item.collection)) {
      const found = findArticleInRawData(targetId, item.collection as unknown[]);
      if (found) foundArticle = found;
    }
  });
  if (foundArticle) return foundArticle;

  return null;
};

// Helper to get embargoed article IDs from API response
const getEmbargoResolvedIds = (embargoArticlesData: EmbargoArticlesData): Set<string> => {
  if (!embargoArticlesData?.pages) return new Set<string>();
  // Each page.data.attributes is an array of articles, each with .data.id
  const ids = embargoArticlesData.pages.flatMap((page) => {
    if (!page.data?.attributes) return [];
    return page.data.attributes.map((item) => item.data?.id).filter(Boolean) as string[];
  });
  return new Set<string>(ids);
};

// Utility to convert array of parameters to an object { name: value }
export const paramsArrayToObject = (
  params: Array<{ type: string; name: string; value: unknown }>
): Record<string, unknown> =>
  params.reduce<Record<string, unknown>>((acc, param) => {
    acc[param.name] = param.value;
    return acc;
  }, {});

// Recursive function to find a raw node by ID in nested collections
export const findRawNode = (rawItems: Article[], targetId: string): Article | null => {
  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let i = 0; i < rawItems.length; i++) {
    const rawItem = rawItems[i];
    if (rawItem.id === targetId) return rawItem;
    if (rawItem.collection && Array.isArray(rawItem.collection)) {
      const found = findRawNode(rawItem.collection, targetId);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Validates article data between raw and resolved collections
 * Returns structured information about which articles should be rendered
 */
export interface ArticleValidationResult {
  /** Total articles expected from raw data */
  rawArticleCount: number;
  /** Total articles available in resolved data */
  resolvedArticleCount: number;
  /** Articles that appear in resolved data (should be rendered) */
  renderableArticles: Set<string>;
  /** Articles in raw but missing from resolved (errors) */
  missingArticles: Set<string>;
  /** Articles that are embargoed */
  embargoedArticles: Set<string>;
  /** Articles successfully rendered (in resolved, not embargoed) */
  successfulArticles: Set<string>;
  /** Embargo articles expected from raw data */
  expectedEmbargoArticles: Set<string>;
  /** Embargo articles missing from embargo API response */
  missingEmbargoArticles: Set<string>;
}

export const validatePageArticles = (
  rawCollectionData: { data?: { attributes?: { collection?: unknown[] } } } | null | undefined,
  resolvedCollectionData: any,
  embargoArticlesData?: EmbargoArticlesData
): ArticleValidationResult => {
  // Extract all article IDs from raw data
  const rawArticleIds = rawCollectionData?.data?.attributes?.collection
    ? extractArticlesFromRawData(rawCollectionData.data.attributes.collection as CollectionOrArticle[])
    : new Set<string>();

  // Extract all article IDs from resolved data
  const resolvedArticleIds = extractArticlesFromResolvedData(resolvedCollectionData);

  // Get embargoed article IDs from API
  const embargoedArticleIds = getEmbargoResolvedIds(embargoArticlesData);

  // Get expected embargo article IDs from raw data
  const expectedEmbargoIds = rawCollectionData?.data?.attributes?.collection
    ? extractEmbargoArticlesFromRawData(rawCollectionData.data.attributes.collection as CollectionOrArticle[])
    : new Set<string>();

  // Calculate missing embargo articles (expected but not in embargo API response)
  const missingEmbargoIds = new Set(Array.from(expectedEmbargoIds).filter((id) => !embargoedArticleIds.has(id)));

  // Calculate missing non-embargo articles (in raw but not in resolved and not expected to be embargo)
  const missingArticleIds = new Set(
    Array.from(rawArticleIds).filter((id) => !resolvedArticleIds.has(id) && !expectedEmbargoIds.has(id))
  );

  // Calculate successful articles (in resolved and not embargoed)
  const successfulArticleIds = new Set(Array.from(resolvedArticleIds).filter((id) => !embargoedArticleIds.has(id)));

  return {
    rawArticleCount: rawArticleIds.size,
    resolvedArticleCount: resolvedArticleIds.size,
    renderableArticles: resolvedArticleIds,
    missingArticles: missingArticleIds,
    embargoedArticles: embargoedArticleIds,
    successfulArticles: successfulArticleIds,
    expectedEmbargoArticles: expectedEmbargoIds,
    missingEmbargoArticles: missingEmbargoIds
  };
};

export interface QueryCountMismatch {
  collectionId: string;
  requested: number;
  actual: number;
  difference: number;
  type: 'missing' | 'extra';
}

export const findQueryCountMismatches = (items: Article[]): QueryCountMismatch[] => {
  const mismatches: QueryCountMismatch[] = [];

  const checkCollection = (item: Article, parentItem?: Article) => {
    // Get parameters
    const params = paramsArrayToObject(item.parameters as Array<{ type: string; name: string; value: unknown }>);

    // Check if this collection has nested collections
    const nestedCollections =
      item.collection && Array.isArray(item.collection)
        ? item.collection.filter((child: Article) => child.type === 'collection')
        : [];

    // Check if nested collections are feeds
    const hasFeedChildren = nestedCollections.some((child: Article) => {
      const childParams = paramsArrayToObject(
        child.parameters as Array<{ type: string; name: string; value: unknown }>
      );
      return childParams.feed_title || childParams.feed_url;
    });

    // Check if this is a non-feed child collection whose query is displayed at parent level
    const isNonFeedChild = parentItem && !params.feed_title && !params.feed_url;

    // Skip validation if:
    // 1. This is a non-feed child (its query is shown at parent level, articles render at parent)
    // 2. This has feed children (articles are distributed to feed children, not at this level)
    const shouldSkipValidation = isNonFeedChild || (hasFeedChildren && nestedCollections.length > 0);

    // Get query count if present and should validate
    if (!shouldSkipValidation && params.query && typeof params.query === 'object') {
      const query = params.query as { count?: number };
      const requestedCount = query.count;

      if (typeof requestedCount === 'number') {
        // Get articles for this collection (filter out nested collections)
        const articles =
          item.collection && Array.isArray(item.collection)
            ? item.collection.filter((child: Article) => child.type !== 'collection')
            : [];

        const actualCount = articles.length;

        if (actualCount !== requestedCount) {
          // Get collection ID
          const collectionId =
            params.module_id || params.group_id || params.treatment_type || item.id || 'Unknown Collection';

          mismatches.push({
            collectionId: String(collectionId),
            requested: requestedCount,
            actual: actualCount,
            difference: Math.abs(requestedCount - actualCount),
            type: actualCount < requestedCount ? 'missing' : 'extra'
          });
        }
      }
    }

    // Recursively check nested collections
    if (item.collection && Array.isArray(item.collection)) {
      item.collection.forEach((child: Article) => {
        if (child.type === 'collection') {
          checkCollection(child, item);
        }
      });
    }
  };

  items.forEach((item) => checkCollection(item));
  return mismatches;
};

export {
  extractArticlesFromResolvedData,
  parseUrlForAllessehId,
  formatDate,
  getEmbargoResolvedIds,
  getAllessehAdminUrl,
  getAllessehSummariesUrl,
  extractArticlesFromRawData,
  findArticleInRawData,
  extractEmbargoArticlesFromRawData
};
